import { defineMessage, IntlShape } from "react-intl";
import { v4 as uuidv4 } from "uuid";
import { CustomBundle } from ".";
import { PropertyData } from "../../components/property-admin-data-provider";
import { IConfig } from "../../lib/config";
import { FormConfiguration } from "../shared/crud/form";
import { RendererProps } from "../shared/renderers";
import { ArrayRenderer } from "../shared/renderers/table";
import { CheckboxInput } from "../shared/widgets/checkboxInput";
import { DatePickerWidget } from "../shared/widgets/datePicker";

import ExtrasMultiCheckbox from "../shared/widgets/extrasMultiCheckbox";
import { HtmlEditorWidget } from "../shared/widgets/html";
import PromotionsMultiCheckbox from "../shared/widgets/promotionsMultiCheckbox";
import SectionUnderline from "../shared/widgets/sectionUnderline";
import { smartSelectorFieldConfig, SmartSelectorItem } from "../shared/widgets/SmartSelector";
import { SwitchGroup } from "../shared/widgets/SwitchGroup";
import { translationFieldConfig } from "../shared/widgets/translation";
import WeekDaysMultiCheckbox from "../shared/widgets/weekDaysMultiCheckbox";
import { ConditionTypeEnum } from "../witadmin/CRUD/Conditions/domain/ConditionType.enum";
import { filterMessages } from "./messages";

export function CustomDateRenderer({ value = "" }: RendererProps<string>) {
  return value ? value.substring(0, 10) : null;
}

export const getTravelWindowsTableColumns = () => {
  return {
    startDate: {
      renderer: CustomDateRenderer,
    },
    endDate: {
      renderer: CustomDateRenderer,
    },
    weekDaysList: {
      renderer: ArrayRenderer,
    },
  };
};

export const getTravelWindowsFormConfiguration = () => {
  return {
    __fieldOrder: ["*"],
    __useProtoStyleArrays: true,
    id: {
      hidden: true,
    },
    startDate: {
      fieldWidget: DatePickerWidget,
      messages: {
        label: defineMessage({ id: "package.startDate", defaultMessage: "startDate" }),
      },
    },
    endDate: {
      fieldWidget: DatePickerWidget,
      messages: {
        label: defineMessage({ id: "package.endDate", defaultMessage: "endDate" }),
      },
    },
    weekDaysList: {
      fieldWidget: WeekDaysMultiCheckbox,
      messages: {
        label: defineMessage({ id: "package.weekDays", defaultMessage: "weekDays" }),
      },
    },
  };
};

export const getAllowedInventoryLines = (
  propertyData: PropertyData | undefined | null
): SmartSelectorItem[] => {
  if (propertyData?.inventories?.length) {
    return (
      propertyData.inventories
        // `=== ENGINE`, not `!== STORE`: bundles must not offer Flight + Hotel inventory lines.
        // See US-001 condition-type audit row 5.
        .filter(i => i.condition.type === ConditionTypeEnum.ENGINE)
        .map(
          inventory =>
            ({
              name: `${inventory.accommodation.name} / ${inventory.configuration.name} , ${inventory.mealPlan.name} , ${inventory.condition.name}`,
              ticker: inventory.ticker,
              filters: {
                accommodation: inventory.accommodation,
                condition: inventory.condition,
                configuration: inventory.configuration,
                mealPlan: inventory.mealPlan,
              },
            } as SmartSelectorItem)
        )
    );
  }
  return [];
};

export const getFormConfiguration = (
  intl: IntlShape,
  allowedLocales: any,
  allowedInventoryLines: any,
  travelWindowsFormConfiguration: any,
  travelWindowsTableColumns: any,
  config: IConfig
): FormConfiguration<CustomBundle & { travelWindowSection?: string }> => {
  return {
    __fieldOrder: [
      "ticker",
      "active",
      "extrasList",
      "translationsList",
      "multimediaList",
      "travelWindowSection",
      "minStay",
      "maxStay",
      "minRelease",
      "maxRelease",
      "promotionsList",
      "bookingWindowsList",
      "codesList",
      "*",
    ],
    __useProtoStyleArrays: true,
    id: {
      default: () => uuidv4(),
      hidden: true,
    },
    ticker: {
      readonly: isNew => !isNew,
      messages: {
        label: defineMessage({ id: "package.ticker", defaultMessage: "Id" }),
      },
    },
    active: {
      messages: {
        label: defineMessage({ id: "package.active", defaultMessage: "active" }),
      },
      default: () => true,
      widget: SwitchGroup,
    },
    translationsList: {
      ...translationFieldConfig(allowedLocales || []),
      children: {
        __fieldOrder: ["name", "description", "*"],
        name: {
          messages: {
            label: defineMessage({ id: "package.translation.name", defaultMessage: "Name" }),
          },
        },
        description: {
          widget: HtmlEditorWidget,
          messages: {
            label: defineMessage({
              id: "package.translation.description",
              defaultMessage: "Description",
            }),
          },
        },
        ...translationFieldConfig(allowedLocales || []).children,
      },
      messages: {
        label: defineMessage({ id: "package.translations", defaultMessage: "Translations" }),
      },
    },
    inventoryLinesList: {
      ...smartSelectorFieldConfig(allowedInventoryLines, {
        filterNames: [
          intl.formatMessage(filterMessages.accommodation),
          intl.formatMessage(filterMessages.conditions),
          intl.formatMessage(filterMessages.ocupation),
          intl.formatMessage(filterMessages.mealplan),
        ],
      }),
      messages: {
        label: defineMessage({ id: "package.inventoryLines", defaultMessage: "Inventory lines" }),
        description: defineMessage({
          id: "package.inventoryLines.description",
          defaultMessage: "This package will only apply to the selected inventory lines.",
        }),
      },
    },

    travelWindowSection: {
      fieldWidget: SectionUnderline,
      messages: {
        label: defineMessage({
          id: "package.travelWindow",
          defaultMessage: "travelWindow",
        }),
      },
    },
    minStay: {
      messages: {
        label: defineMessage({ id: "package.minStay", defaultMessage: "minStay" }),
      },
      widget: CheckboxInput,
    },
    maxStay: {
      messages: {
        label: defineMessage({ id: "package.maxStay", defaultMessage: "maxStay" }),
      },
      widget: CheckboxInput,
    },
    minRelease: {
      messages: {
        label: defineMessage({ id: "package.minRelease", defaultMessage: "minRelease" }),
      },
      widget: CheckboxInput,
    },
    maxRelease: {
      messages: {
        label: defineMessage({ id: "package.maxRelease", defaultMessage: "maxRelease" }),
      },
      widget: CheckboxInput,
    },
    extrasList: {
      messages: {
        label: defineMessage({ id: "package.extras", defaultMessage: "extras" }),
      },
      fieldWidget: ExtrasMultiCheckbox,
    },
    promotionsList: {
      messages: {
        label: defineMessage({ id: "package.promotions", defaultMessage: "promotions" }),
      },
      fieldWidget: PromotionsMultiCheckbox,
    },
    travelWindowsList: {
      id: "root_travelWindowList",
      nestedFormConfiguration: travelWindowsFormConfiguration,
      nestedTableColumns: travelWindowsTableColumns,
      nestedEntityName: {
        defaultMessage: "bookingWindow",
        id: "package.bookingWindow",
      },
    },
  };
};
